import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { ResponsiveBaseComponent } from 'shared-lib';
import {
  EditParkingOperatorRequest,
  ParkingService,
  ParkingOperatorDto
} from '../../../../api/gen';


@Component({
  selector: 'app-operator-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule
  ],
  templateUrl: './operator-edit.component.html',
  styleUrl: './operator-edit.component.scss'
})
export class OperatorEditComponent extends ResponsiveBaseComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private parkingService = inject(ParkingService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);

  editOperatorForm: FormGroup;
  operatorId: string = '';
  operator: ParkingOperatorDto | null = null;
  isLoading = false;
  isSubmitting = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  constructor() {
    super();
    this.editOperatorForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
      enabled: [true]
    });
  }

  ngOnInit(): void {
    this.operatorId = this.route.snapshot.paramMap.get('id') || '';
    if (this.operatorId) {
      this.loadOperator();
    } else {
      this.errorMessage = 'No operator ID provided';
    }
  }

  private loadOperator(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.parkingService.parkingOperatorParkingOperatorIdGet(this.operatorId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response) {
          this.operator = response;
          this.populateForm(response);
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        this.errorMessage = error.message || 'An error occurred while loading the operator. Please try again.';
        console.error('Error loading operator:', error);
      }
    });
  }

  private populateForm(operator: ParkingOperatorDto): void {
    this.editOperatorForm.patchValue({
      firstName: operator.firstName || '',
      lastName: operator.lastName || '',
      email: operator.email || '',
      enabled: operator.enabled || false
    });
  }

  onSubmit(): void {
    if (this.editOperatorForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      this.errorMessage = null;
      this.successMessage = null;

      const formValue = this.editOperatorForm.value;

      const editRequest: EditParkingOperatorRequest = {
        firstName: formValue.firstName?.trim(),
        lastName: formValue.lastName?.trim(),
        email: formValue.email?.trim(),
        enabled: formValue.enabled
      };

      this.parkingService.parkingOperatorParkingOperatorIdPut(this.operatorId, editRequest).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response) {
            this.operator = response;
            this.successMessage = 'Operator updated successfully!';

            // Show success snackbar
            this.snackBar.open('Operator updated successfully!', 'Close', {
              duration: 30000,
              panelClass: ['success-snackbar']
            });

            // Navigate back after a brief delay
            setTimeout(() => {
              this.router.navigate(['/user-management/operator-management']);
            }, 1500);
          }
        },
        error: (error: any) => {
          this.isSubmitting = false;
          this.errorMessage = error.message || 'An error occurred while updating the operator. Please try again.';
          console.error('Error updating operator:', error);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.editOperatorForm);
    }
  }

  onCancel(): void {
    this.router.navigate(['/user-management/operator-management']);
  }

  onClearError(): void {
    this.errorMessage = null;
  }

  onClearSuccess(): void {
    this.successMessage = null;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.editOperatorForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.editOperatorForm.get(fieldName);
    if (field && field.errors && field.touched) {
      if (field.errors['required']) return `${this.getFieldLabel(fieldName)} is required`;
      if (field.errors['email']) return 'Please enter a valid email address';
      if (field.errors['minlength']) return `${this.getFieldLabel(fieldName)} is too short`;
      if (field.errors['maxlength']) return `${this.getFieldLabel(fieldName)} is too long`;
    }
    return '';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      email: 'Email',
      firstName: 'First Name',
      lastName: 'Last Name',
      enabled: 'Status'
    };
    return labels[fieldName] || fieldName;
  }

  getOperatorDisplayName(): string {
    if (!this.operator) return 'Unknown Operator';
    const firstName = this.operator.firstName || '';
    const lastName = this.operator.lastName || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Operator';
  }
}
